import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Palette,
  Wrench,
  Camera,
  Beaker,
  Hammer,
  Car,
  Utensils,
  Zap,
  Music,
  Mountain,
  Scissors,
  Cpu,
  Search,
  Filter,
  ArrowRight,
  Star,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  Info,
  Eye
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { templatesApi } from '@/services/api'
import { TemplatePreviewModal } from '@/components/templates/TemplatePreviewModal'

interface BusinessTemplate {
  id: string
  name: string
  businessType: string
  description: string
  industry: string
  complexity: 'beginner' | 'intermediate' | 'advanced'
  tags: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
  // Preview data
  categoriesCount?: number
  projectTypesCount?: number
  activityTypesCount?: number
  unitTypesCount?: number
  // Usage stats
  usageCount?: number
  rating?: number
}

interface TemplateSelectionPageProps {
  mode?: 'onboarding' | 'switch'
  currentTemplateId?: string
  onTemplateSelected?: (templateId: string) => void
}

// Industry icons mapping
const industryIcons: Record<string, React.ComponentType<any>> = {
  fashion: Palette,
  crafts: Hammer,
  technology: Cpu,
  food: Utensils,
  automotive: Car,
  arts: Camera,
  music: Music,
  outdoor: Mountain,
  textiles: Scissors,
  electronics: Zap,
  science: Beaker,
  tools: Wrench,
}

// Complexity colors
const complexityColors = {
  beginner: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  intermediate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  advanced: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

export const TemplateSelectionPage: React.FC<TemplateSelectionPageProps> = ({
  mode = 'onboarding',
  currentTemplateId,
  onTemplateSelected
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [templates, setTemplates] = useState<BusinessTemplate[]>([])
  const [filteredTemplates, setFilteredTemplates] = useState<BusinessTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [industryFilter, setIndustryFilter] = useState('')
  const [complexityFilter, setComplexityFilter] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<BusinessTemplate | null>(null)
  const [isAssigning, setIsAssigning] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState<BusinessTemplate | null>(null)
  const [showPreview, setShowPreview] = useState(false)

  // Get unique industries and complexities for filters
  const industries = [...new Set(templates.map(t => t.industry).filter(Boolean))].sort()
  const complexities = ['beginner', 'intermediate', 'advanced']

  useEffect(() => {
    fetchTemplates()
  }, [])

  useEffect(() => {
    filterTemplates()
  }, [templates, searchTerm, industryFilter, complexityFilter])

  const fetchTemplates = async () => {
    try {
      setLoading(true)
      
      // Use optimized bulk preview endpoint that gets everything in one call
      const templatesData = await templatesApi.getBulkPreview()
      const templates = Array.isArray(templatesData) ? templatesData : templatesData.data || []
      
      setTemplates(templates)
      
    } catch (error) {
      console.error('Error fetching templates:', error)
      // Fallback to individual calls if bulk endpoint fails
      try {
        const response = await templatesApi.getAll()
        const fallbackTemplates = Array.isArray(response) ? response : response.data || []
        setTemplates(fallbackTemplates.map((template: BusinessTemplate) => ({
          ...template,
          categoriesCount: 0,
          projectTypesCount: 0,
          activityTypesCount: 0,
          unitTypesCount: 0,
          usageCount: 0,
          rating: 0,
        })))
      } catch (fallbackError) {
        console.error('Fallback template fetch also failed:', fallbackError)
        setTemplates([])
      }
    } finally {
      setLoading(false)
    }
  }

  const filterTemplates = () => {
    let filtered = templates

    if (searchTerm) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (template.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    if (industryFilter) {
      filtered = filtered.filter(template => template.industry === industryFilter)
    }

    if (complexityFilter) {
      filtered = filtered.filter(template => template.complexity === complexityFilter)
    }

    // Sort by usage count and rating
    filtered.sort((a, b) => {
      const aScore = (a.usageCount || 0) * 0.7 + (a.rating || 0) * 0.3
      const bScore = (b.usageCount || 0) * 0.7 + (b.rating || 0) * 0.3
      return bScore - aScore
    })

    setFilteredTemplates(filtered)
  }

  const handleTemplateSelect = async (template: BusinessTemplate) => {
    if (isAssigning) return

    try {
      setIsAssigning(true)
      setSelectedTemplate(template)

      if (mode === 'switch') {
        // For existing users switching templates
        await templatesApi.switchTemplate(template.id, {
          preserveData: true,
          migrationStrategy: 'merge'
        })
        
        // Notify all components that the template has changed
        window.dispatchEvent(new CustomEvent('templateChanged', {
          detail: { templateId: template.id, template }
        }))
      } else {
        // For new users during onboarding
        await templatesApi.assignToUser(template.id)
      }

      if (onTemplateSelected) {
        onTemplateSelected(template.id)
      } else {
        // Navigate to dashboard or next step
        const returnTo = searchParams.get('returnTo') || '/dashboard'
        navigate(returnTo)
      }
    } catch (error) {
      console.error('Error assigning template:', error)
      alert('Failed to select template. Please try again.')
    } finally {
      setIsAssigning(false)
      setSelectedTemplate(null)
    }
  }

  const handlePreviewTemplate = (template: BusinessTemplate, e: React.MouseEvent) => {
    e.stopPropagation()
    setPreviewTemplate(template)
    setShowPreview(true)
  }

  const handleClosePreview = () => {
    setShowPreview(false)
    setPreviewTemplate(null)
  }

  const getTemplateIcon = (template: BusinessTemplate) => {
    const IconComponent = industryIcons[template.industry] || Hammer
    return <IconComponent className="w-8 h-8" />
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading templates...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>
          {mode === 'switch' ? 'Switch Business Template' : 'Choose Your Business Template'} - Hobby Business Tracker
        </title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {mode === 'switch' ? 'Switch Your Business Template' : 'Choose Your Business Template'}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              {mode === 'switch' 
                ? 'Select a new template to switch your hobby business focus. Your existing data will be preserved and adapted to the new template.'
                : 'Select the template that best matches your hobby business. This will customize categories, project types, and workflows specifically for your industry.'
              }
            </p>
          </div>

          {/* Filters */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search templates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select
                  value={industryFilter}
                  onValueChange={setIndustryFilter}
                  className="min-w-[150px]"
                >
                  <option value="">All Industries</option>
                  {industries.map(industry => (
                    <option key={industry} value={industry}>
                      {industry ? industry.charAt(0).toUpperCase() + industry.slice(1) : 'Unknown'}
                    </option>
                  ))}
                </Select>

                <Select
                  value={complexityFilter}
                  onValueChange={setComplexityFilter}
                  className="min-w-[150px]"
                >
                  <option value="">All Levels</option>
                  {complexities.map(complexity => (
                    <option key={complexity} value={complexity}>
                      {complexity.charAt(0).toUpperCase() + complexity.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="text-sm text-gray-500 dark:text-gray-400">
                {filteredTemplates.length} of {templates.length} templates
              </div>
            </div>
          </div>

          {/* Templates Grid */}
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <Filter className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No templates found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your search criteria or filters
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => (
                <Card
                  key={template.id}
                  className={`p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                    currentTemplateId === template.id
                      ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'hover:border-blue-300 dark:hover:border-blue-600'
                  } ${
                    isAssigning && selectedTemplate?.id === template.id
                      ? 'opacity-75 cursor-not-allowed'
                      : ''
                  }`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  {/* Template Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                        {getTemplateIcon(template)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {template.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                          {template.industry}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handlePreviewTemplate(template, e)}
                        className="p-1 h-8 w-8"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {currentTemplateId === template.id && (
                        <Badge variant="success" size="sm">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Current
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
                    {template.description}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {template.categoriesCount || 0}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">Categories</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {template.projectTypesCount || 0}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">Project Types</div>
                    </div>
                  </div>

                  {/* Tags and Complexity */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge 
                      className={complexityColors[template.complexity]}
                      size="sm"
                    >
                      {template.complexity}
                    </Badge>
                    {template.tags && template.tags.slice(0, 2).map((tag) => (
                      <Badge key={tag} variant="secondary" size="sm">
                        {tag}
                      </Badge>
                    ))}
                    {template.tags && template.tags.length > 2 && (
                      <Badge variant="secondary" size="sm">
                        +{template.tags.length - 2} more
                      </Badge>
                    )}
                  </div>

                  {/* Usage Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                    <div className="flex items-center space-x-1">
                      <Users className="w-3 h-3" />
                      <span>{template.usageCount || 0} users</span>
                    </div>
                    {template.rating && template.rating > 0 && (
                      <div className="flex items-center space-x-1">
                        <Star className="w-3 h-3 fill-current text-yellow-400" />
                        <span>{template.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <Button
                    className="w-full"
                    disabled={isAssigning}
                    variant={currentTemplateId === template.id ? "outline" : "primary"}
                  >
                    {isAssigning && selectedTemplate?.id === template.id ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {mode === 'switch' ? 'Switching...' : 'Selecting...'}
                      </>
                    ) : currentTemplateId === template.id ? (
                      'Current Template'
                    ) : (
                      <>
                        {mode === 'switch' ? 'Switch to This Template' : 'Select This Template'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </Card>
              ))}
            </div>
          )}

          {/* Help Section */}
          {mode === 'onboarding' && (
            <div className="mt-12 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div>
                  <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2">
                    Need help choosing?
                  </h3>
                  <p className="text-blue-800 dark:text-blue-200 mb-4">
                    Don't worry! You can always switch to a different template later from your settings. 
                    Choose the one that feels closest to your current hobby business focus.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="font-medium text-blue-900 dark:text-blue-100">Beginner</div>
                      <div className="text-blue-700 dark:text-blue-300">Simple workflows, basic features</div>
                    </div>
                    <div>
                      <div className="font-medium text-blue-900 dark:text-blue-100">Intermediate</div>
                      <div className="text-blue-700 dark:text-blue-300">Balanced features, moderate complexity</div>
                    </div>
                    <div>
                      <div className="font-medium text-blue-900 dark:text-blue-100">Advanced</div>
                      <div className="text-blue-700 dark:text-blue-300">Full features, complex workflows</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Template Preview Modal */}
          <TemplatePreviewModal
            template={previewTemplate}
            isOpen={showPreview}
            onClose={handleClosePreview}
            onSelect={handleTemplateSelect}
            currentTemplateId={currentTemplateId}
            mode={mode}
          />
        </div>
      </div>
    </>
  )
}

export default TemplateSelectionPage